import React, { useState, useEffect, useCallback } from 'react';
import { testimonials } from '../data/mockData';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const TestimonialCard = ({ t }) => (
  <div className="bg-[#093336]/60 backdrop-blur-sm border border-white/5 rounded-2xl p-7 h-full flex flex-col">
    <div className="flex items-center gap-1 mb-4">
      {[...Array(t.rating)].map((_, i) => <Star key={i} className="w-4 h-4 fill-[#A5BF23] text-[#A5BF23]" />)}
    </div>
    <Quote className="w-6 h-6 text-[#A5BF23]/15 mb-2" strokeWidth={1.5} />
    <blockquote className="text-sm text-[#A0B2B2] leading-relaxed mb-6 flex-1">{t.content}</blockquote>
    <div className="flex items-center gap-3 pt-4 border-t border-white/5">
      <div className="w-10 h-10 rounded-full bg-[#A5BF23]/10 flex items-center justify-center text-[#A5BF23] font-bold text-sm">{t.name.charAt(0)}</div>
      <div>
        <div className="font-semibold text-sm text-[#F0F4F4]">{t.name}</div>
        <div className="text-xs text-[#A0B2B2]/60">{t.role}</div>
      </div>
    </div>
  </div>
);

const TestimonialsSection = () => {
  const header = useScrollAnimation({ threshold: 0.15 });
  const [current, setCurrent] = useState(0);
  const items = testimonials.slice(0, 6);
  const totalSlides = Math.ceil(items.length / 3);

  const next = useCallback(() => setCurrent(prev => (prev + 1) % totalSlides), [totalSlides]);
  const prev = () => setCurrent(prev => (prev - 1 + totalSlides) % totalSlides);

  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);

  return (
    <section id="testimonials" data-testid="testimonials-section" className="py-24 md:py-32 bg-[#041B1C] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div ref={header.ref} className={`text-center max-w-2xl mx-auto mb-16 scroll-fade-up ${header.isVisible ? 'scroll-visible' : ''}`}>
          <span className="inline-block bg-[#A5BF23]/10 text-[#A5BF23] px-4 py-1.5 rounded-full text-xs font-bold tracking-[0.2em] mb-4">CLIENT REVIEWS</span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#F0F4F4] mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>What Brand Owners Say About Us</h2>
          <p className="text-base text-[#A0B2B2] leading-relaxed">Real reviews from business owners who registered their brand names with us.</p>
        </div>

        <div className="relative">
          <div className="overflow-hidden">
            <div className="flex transition-transform duration-500 ease-in-out" style={{ transform: `translateX(-${current * 100}%)` }}>
              {Array.from({ length: totalSlides }).map((_, slideIdx) => (
                <div key={slideIdx} className="w-full flex-shrink-0">
                  <div className="grid md:grid-cols-3 gap-6">
                    {items.slice(slideIdx * 3, slideIdx * 3 + 3).map(t => (
                      <TestimonialCard key={t.id} t={t} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-center gap-4 mt-10">
            <button data-testid="testimonial-prev" onClick={prev} className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-[#A0B2B2] hover:border-[#A5BF23]/50 hover:text-[#A5BF23] transition-all duration-300">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex gap-2">
              {Array.from({ length: totalSlides }).map((_, i) => (
                <button key={i} onClick={() => setCurrent(i)} className={`w-2 h-2 rounded-full transition-all duration-300 ${i === current ? 'bg-[#A5BF23] w-6' : 'bg-white/20 hover:bg-white/40'}`} />
              ))}
            </div>
            <button data-testid="testimonial-next" onClick={next} className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-[#A0B2B2] hover:border-[#A5BF23]/50 hover:text-[#A5BF23] transition-all duration-300">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
